"use client";

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Badge } from "@/app/components/ui/badge";
import { Progress } from "@/app/components/ui/progress";
import { Alert, AlertDescription } from "@/app/components/ui/alert";
import { 
  Activity, 
  Users, 
  Gamepad2, 
  Clock, 
  Trophy,
  User,
  Calendar,
  MapPin,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  GamepadIcon,
  TrendingUp,
  Award,
  Star,
  Play,
  Pause,
  Globe,
  Shield,
  Eye,
  EyeOff,
} from "lucide-react";
import { toast } from "sonner";

interface SteamData {
  profile: {
    steamId: string;
    personaName: string;
    realName?: string;
    profileUrl: string;
    avatar: string;
    avatarMedium: string;
    avatarFull: string;
    personaState: number;
    communityVisibilityState: number;
    profileState: number;
    lastLogoff: number;
    commentPermission: number;
    timeCreated: number;
    locCountryCode?: string;
    locStateCode?: string;
    locCityId?: number;
    gameExtraInfo?: string;
    gameId?: string;
  };
  games: {
    totalGames: number;
    totalPlaytime: number;
    ownedGames: Array<{
      appId: number;
      name: string;
      playtimeForever: number;
      playtimeWindows: number;
      playtimeMac: number;
      playtimeLinux: number;
      rtimeLastPlayed: number;
      playtimeDisconnected: number;
      hasCommunityVisibleStats: boolean;
      playtime2weeks: number;
      imgIconUrl: string;
      imgLogoUrl: string;
    }>;
  };
  recentActivity: {
    recentGames: Array<{
      appId: number;
      name: string;
      playtime2weeks: number;
      playtimeForever: number;
      imgIconUrl: string;
      imgLogoUrl: string;
    }>;
  };
  social: {
    friendsCount: number;
    friends: Array<{
      steamId: string;
      relationship: string;
      friendSince: number;
    }>;
  };
  achievements: any;
}

export default function SteamAnalytics() {
  const [steamId, setSteamId] = useState("");
  const [data, setData] = useState<SteamData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedGame, setSelectedGame] = useState<any>(null);
  const [gameNews, setGameNews] = useState<any>(null);
  const [globalAchievements, setGlobalAchievements] = useState<any>(null);
  const [playerAchievements, setPlayerAchievements] = useState<any>(null);
  const [userStats, setUserStats] = useState<any>(null);
  const [gameUrl, setGameUrl] = useState("");
  const [gameAnalytics, setGameAnalytics] = useState<any>(null);

  // TRPC mutations
  const analyzeSteamMutation = trpc.analytics.steam.analyze.useMutation();
  const getGameDetailsMutation = trpc.analytics.steam.getGameDetails.useMutation();
  const getGameNewsMutation = trpc.analytics.steam.getGameNews.useMutation();
  const getGlobalAchievementsMutation = trpc.analytics.steam.getGlobalAchievementPercentages.useMutation();
  const getPlayerAchievementsMutation = trpc.analytics.steam.getPlayerAchievements.useMutation();
  const getUserStatsMutation = trpc.analytics.steam.getUserStatsForGame.useMutation();
  const getGameAnalyticsMutation = trpc.analytics.steam.getGameAnalytics.useMutation();
  const analyzeGameFromUrlMutation = trpc.analytics.steam.analyzeGameFromUrl.useMutation();

  // Format duration from minutes to readable format (days, weeks, months)
  const formatPlaytime = (minutes: number) => {
    if (minutes === 0) return "0m";
    
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);
    
    if (years > 0) {
      const remainingMonths = Math.floor((days % 365) / 30);
      if (remainingMonths > 0) {
        return `${years}y ${remainingMonths}mo`;
      }
      return `${years}y`;
    }
    
    if (months > 0) {
      const remainingWeeks = Math.floor((days % 30) / 7);
      if (remainingWeeks > 0) {
        return `${months}mo ${remainingWeeks}w`;
      }
      return `${months}mo`;
    }
    
    if (weeks > 0) {
      const remainingDays = days % 7;
      if (remainingDays > 0) {
        return `${weeks}w ${remainingDays}d`;
      }
      return `${weeks}w`;
    }
    
    if (days > 0) {
      const remainingHours = hours % 24;
      if (remainingHours > 0) {
        return `${days}d ${remainingHours}h`;
      }
      return `${days}d`;
    }
    
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
    }
    return `${mins}m`;
  };
  
  // Format playtime for detailed display
  const formatPlaytimeDetailed = (minutes: number) => {
    if (minutes === 0) return { primary: "0", unit: "minutes" };
    
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);
    
    if (years > 0) {
      return { primary: years.toString(), unit: years === 1 ? "year" : "years", secondary: `${Math.floor((days % 365) / 30)}mo` };
    }
    if (months > 0) {
      return { primary: months.toString(), unit: months === 1 ? "month" : "months", secondary: `${Math.floor((days % 30) / 7)}w` };
    }
    if (weeks > 0) {
      return { primary: weeks.toString(), unit: weeks === 1 ? "week" : "weeks", secondary: `${days % 7}d` };
    }
    if (days > 0) {
      return { primary: days.toString(), unit: days === 1 ? "day" : "days", secondary: `${hours % 24}h` };
    }
    if (hours > 0) {
      return { primary: hours.toString(), unit: hours === 1 ? "hour" : "hours", secondary: `${minutes % 60}m` };
    }
    return { primary: minutes.toString(), unit: minutes === 1 ? "minute" : "minutes" };
  };

  // Format timestamp to readable date
  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get persona state text
  const getPersonaState = (state: number) => {
    const states = {
      0: "Offline",
      1: "Online",
      2: "Busy",
      3: "Away",
      4: "Snooze",
      5: "Looking to trade",
      6: "Looking to play"
    };
    return states[state as keyof typeof states] || "Unknown";
  };

  // Get persona state color
  const getPersonaStateColor = (state: number) => {
    const colors = {
      0: "bg-gray-500",
      1: "bg-green-500",
      2: "bg-red-500",
      3: "bg-yellow-500",
      4: "bg-blue-500",
      5: "bg-purple-500",
      6: "bg-orange-500"
    };
    return colors[state as keyof typeof colors] || "bg-gray-500";
  };

  // Get community visibility text
  const getVisibilityText = (state: number) => {
    const states = {
      1: "Private",
      2: "Friends Only",
      3: "Public"
    };
    return states[state as keyof typeof states] || "Unknown";
  };

  // Get community visibility icon
  const getVisibilityIcon = (state: number) => {
    if (state === 1) return <EyeOff className="h-4 w-4" />;
    if (state === 2) return <Users className="h-4 w-4" />;
    return <Eye className="h-4 w-4" />;
  };

  const handleAnalyze = async () => {
    if (!steamId) return;
    
    setError(null);
    setData(null);

    try {
      const result = await analyzeSteamMutation.mutateAsync({ 
        steamId: steamId 
      });
      console.log("Steam TRPC response:", result);
      
      // Map the API response to match the expected interface
      const typedData: SteamData = {
        profile: {
          steamId: result.data.profile.steamid,
          personaName: result.data.profile.personaname,
          realName: result.data.profile.realname,
          profileUrl: result.data.profile.profileurl,
          avatar: result.data.profile.avatar || '',
          avatarMedium: result.data.profile.avatarmedium || '',
          avatarFull: result.data.profile.avatarfull,
          personaState: result.data.profile.personastate || 0,
          communityVisibilityState: result.data.profile.communityvisibilitystate,
          profileState: result.data.profile.profilestate || 0,
          lastLogoff: result.data.profile.lastlogoff || 0,
          commentPermission: result.data.profile.commentpermission || 0,
          timeCreated: result.data.profile.timecreated || 0,
          locCountryCode: result.data.profile.loccountrycode,
          locStateCode: result.data.profile.locstatecode,
          locCityId: result.data.profile.loccityid,
          gameExtraInfo: result.data.profile.gameextrainfo,
          gameId: result.data.profile.gameid,
        },
        games: {
          totalGames: result.data.games.totalGames,
          totalPlaytime: result.data.games.totalPlaytime,
          ownedGames: result.data.games.ownedGames.map((game: any) => ({
            appId: game.appid,
            name: game.name || 'Unknown Game',
            playtimeForever: game.playtime_forever || 0,
            playtimeWindows: game.playtime_windows_forever || 0,
            playtimeMac: game.playtime_mac_forever || 0,
            playtimeLinux: game.playtime_linux_forever || 0,
            rtimeLastPlayed: game.rtime_last_played || 0,
            playtimeDisconnected: game.playtime_disconnected || 0,
            hasCommunityVisibleStats: game.has_community_visible_stats || false,
            playtime2weeks: game.playtime_2weeks || 0,
            imgIconUrl: game.img_icon_url || '',
            imgLogoUrl: game.img_logo_url || '',
          }))
        },
        recentActivity: {
          recentGames: result.data.recentActivity.recentGames.map((game: any) => ({
            appId: game.appid,
            name: game.name,
            playtime2weeks: game.playtime_2weeks,
            playtimeForever: game.playtime_forever,
            imgIconUrl: game.img_icon_url || '',
            imgLogoUrl: game.img_logo_url || '',
          }))
        },
        social: {
          friendsCount: result.data.social.friendsCount,
          friends: result.data.social.friends.map((friend: any) => ({
            steamId: friend.steamid,
            relationship: friend.relationship,
            friendSince: friend.friend_since,
          }))
        },
        achievements: null // Will be populated separately when needed
      };
      
      setData(typedData);
      toast.success("Fetched Steam analytics successfully", {
        icon: <CheckCircle className="h-4 w-4 text-green-600" />,
        duration: 3000,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleGetGameDetails = async (appId: number) => {
    setError(null);
    try {
      const result = await getGameDetailsMutation.mutateAsync({
        appId: appId.toString(),
      });
      
      if (result.success) {
        setSelectedGame(result.data);
        toast.success("Fetched game details successfully", {
          icon: <CheckCircle className="h-4 w-4 text-green-600" />,
          duration: 3000,
        });
      } else {
        setError("Failed to fetch game details");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleGetGameNews = async (appId: number) => {
    setError(null);
    try {
      const result = await getGameNewsMutation.mutateAsync({
        appId: appId.toString(),
        count: 5,
        maxLength: 300,
      });
      
      if (result.success) {
        setGameNews(result.data);
        toast.success("Fetched game news successfully", {
          icon: <CheckCircle className="h-4 w-4 text-green-600" />,
          duration: 3000,
        });
      } else {
        setError("Failed to fetch game news");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleGetGlobalAchievements = async (appId: number) => {
    setError(null);
    
    // Validate appId
    if (!appId || isNaN(appId)) {
      setError("Invalid App ID provided");
      return;
    }
    
    try {
      const result = await getGlobalAchievementsMutation.mutateAsync({
        appId: appId.toString(),
      });
      
      if (result.success) {
        setGlobalAchievements(result.data);
        toast.success("Fetched global achievements successfully", {
          icon: <CheckCircle className="h-4 w-4 text-green-600" />,
          duration: 3000,
        });
      } else {
        setError("Failed to fetch global achievements");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleGetPlayerAchievements = async (appId: number, steamId: string) => {
    setError(null);
    try {
      const result = await getPlayerAchievementsMutation.mutateAsync({
        appId: appId.toString(),
        steamId: steamId,
        language: "english",
      });
      
      if (result.success) {
        setPlayerAchievements(result.data);
        toast.success("Fetched player achievements successfully", {
          icon: <CheckCircle className="h-4 w-4 text-green-600" />,
          duration: 3000,
        });
      } else {
        setError("Failed to fetch player achievements");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleGetUserStats = async (appId: number, steamId: string) => {
    setError(null);
    try {
      const result = await getUserStatsMutation.mutateAsync({
        appId: appId.toString(),
        steamId: steamId,
      });
      
      if (result.success) {
        setUserStats(result.data);
        toast.success("Fetched user stats successfully", {
          icon: <CheckCircle className="h-4 w-4 text-green-600" />,
          duration: 3000,
        });
      } else {
        setError("Failed to fetch user stats");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleGetComprehensiveGameAnalytics = async (appId: number) => {
    setError(null);
    try {
      const result = await getGameAnalyticsMutation.mutateAsync({
        appId: appId.toString(),
        steamId: data?.profile.steamId,
      });
      
      if (result.success) {
        // Update all the individual states with the comprehensive data
        setSelectedGame(result.data.app);
        setGameNews({ newsitems: result.data.news });
        setGlobalAchievements({ achievements: result.data.globalAchievements });
        if (result.data.user) {
          setPlayerAchievements(result.data.user.achievements);
          setUserStats(result.data.user.stats);
        }
        toast.success("Fetched comprehensive game analytics successfully", {
          icon: <CheckCircle className="h-4 w-4 text-green-600" />,
          duration: 3000,
        });
      } else {
        setError("Failed to fetch comprehensive game analytics");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleAnalyzeGameFromUrl = async () => {
    if (!gameUrl) return;
    
    setError(null);
    try {
      const result = await analyzeGameFromUrlMutation.mutateAsync({
        gameUrl: gameUrl,
      });
      
      if (result.success) {
        setGameAnalytics(result.data);
        // Also update the selected game for the existing UI
        setSelectedGame(result.data.app);
        setGameNews({ newsitems: result.data.news });
        setGlobalAchievements({ achievements: result.data.globalAchievements });
        toast.success("Fetched game analytics from URL successfully", {
          icon: <CheckCircle className="h-4 w-4 text-green-600" />,
          duration: 3000,
        });
      } else {
        setError("Failed to fetch game analytics from URL");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  // Proxy Steam images to avoid CORS issues
  const getProxiedImageUrl = (url: string) => {
    if (url.includes('avatars.steamstatic.com') || 
        url.includes('media.steampowered.com') || 
        url.includes('steamcdn-a.akamaihd.net') ||
        url.includes('shared.akamai.steamstatic.com') ||
        url.includes('steamstatic.com') ||
        url.includes('steampowered.com')) {
      // Use our own image proxy endpoint
      return `/api/proxy-image?url=${encodeURIComponent(url)}`;
    }
    return url;
  };

  // Handle image loading errors with a simple fallback
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const img = e.target as HTMLImageElement;
    // If the proxied image fails, try the original URL directly
    if (img.src.includes('/api/proxy-image')) {
      const urlParam = new URLSearchParams(img.src.split('?')[1]).get('url');
      if (urlParam) {
        img.src = urlParam;
      }
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight mb-2">Steam Analytics</h1>
        <p className="text-muted-foreground">
          Comprehensive Steam profile analysis including gaming statistics, achievements, social connections, and detailed game insights
        </p>
      </div>

      {/* Input Section */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gamepad2 className="h-5 w-5" />
            Steam Profile Analysis
          </CardTitle>
        <CardDescription>
          Analyze Steam profiles to view gaming statistics, achievements, and social data. Enter a Steam ID, custom URL, or Steam profile URL.
        </CardDescription>
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">What you'll get:</h4>
          <ul className="text-xs text-blue-800 space-y-1">
            <li>• <strong>Profile Overview:</strong> Avatar, status, location, and account details</li>
            <li>• <strong>Gaming Statistics:</strong> Total games, playtime, and recent activity</li>
            <li>• <strong>Game Library:</strong> Complete list of owned games with playtime data</li>
            <li>• <strong>Social Connections:</strong> Friends list and social statistics</li>
            <li>• <strong>Game Analytics:</strong> Detailed insights for individual games including news, achievements, and statistics</li>
          </ul>
        </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label htmlFor="steam-id">Steam Profile Identifier</Label>
              <Input
                id="steam-id"
                placeholder="Enter Steam ID, custom URL, or profile URL..."
                value={steamId}
                onChange={(e) => setSteamId(e.target.value)}
                className="mt-1"
              />
              <div className="mt-2 space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Valid formats:</p>
                <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                  <li>• <strong>Steam ID:</strong> 76561198000000000</li>
                  <li>• <strong>Custom URL:</strong> yourusername</li>
                  <li>• <strong>Profile URL:</strong> https://steamcommunity.com/id/yourusername</li>
                  <li>• <strong>Profile URL:</strong> https://steamcommunity.com/profiles/76561198000000000</li>
                </ul>
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                  <p className="text-xs text-amber-800">
                    <strong>⚠️ Important:</strong> Use Steam profile URLs (steamcommunity.com), not Steam Store URLs (store.steampowered.com). 
                    Profile URLs contain "/id/" or "/profiles/" in the path.
                  </p>
                </div>
              </div>
            </div>
            
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button
              onClick={handleAnalyze} 
              disabled={!steamId || analyzeSteamMutation.isPending}
              className="min-w-[120px]"
            >
              {analyzeSteamMutation.isPending ? (
                <>
                  <Activity className="h-4 w-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Gamepad2 className="h-4 w-4 mr-2" />
                  Analyze Profile
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Game Analysis Section */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gamepad2 className="h-5 w-5" />
            Game Analysis from Steam Store URL
          </CardTitle>
          <CardDescription>
            Analyze any Steam game directly from its store page URL to get comprehensive game data, news, and statistics
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label htmlFor="game-url">Steam Store URL</Label>
              <Input
                id="game-url"
                placeholder="https://store.steampowered.com/app/123456/GameName/"
                value={gameUrl}
                onChange={(e) => setGameUrl(e.target.value)}
                className="mt-1"
              />
              <div className="mt-2 space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Valid Steam Store URL formats:</p>
                <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                  <li>• https://store.steampowered.com/app/123456/GameName/</li>
                  <li>• https://store.steampowered.com/app/123456/</li>
                </ul>
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-xs text-green-800">
                    <strong>✅ Perfect for:</strong> Analyzing any Steam game without needing a profile. Get game details, current players, achievements, and news.
                  </p>
                </div>
              </div>
            </div>

            <Button
              onClick={handleAnalyzeGameFromUrl} 
              disabled={!gameUrl || analyzeGameFromUrlMutation.isPending}
              className="min-w-[120px]"
            >
              {analyzeGameFromUrlMutation.isPending ? (
                <>
                  <Activity className="h-4 w-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Gamepad2 className="h-4 w-4 mr-2" />
                  Analyze Game
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results Section */}
      {(data || gameAnalytics) && (
        <div className="space-y-8">
          {/* Profile Overview or Game Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {data ? <User className="h-5 w-5" /> : <Gamepad2 className="h-5 w-5" />}
                {data ? 'Profile Overview' : 'Game Overview'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data ? (
                <div className="flex items-start gap-6">
                  <div className="relative shrink-0">
                    <img
                      src={getProxiedImageUrl(data.profile.avatarFull)}
                      alt={data.profile.personaName}
                      className="w-28 h-28 rounded-full object-cover border-4 border-background shadow-lg"
                      onError={handleImageError}
                    />
                    <div className={`absolute bottom-0 right-0 w-6 h-6 rounded-full border-4 border-background ${getPersonaStateColor(data.profile.personaState)}`}></div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h2 className="text-3xl font-bold">{data.profile.personaName}</h2>
                      <Badge variant="secondary" className="text-sm">
                        {getPersonaState(data.profile.personaState)}
                      </Badge>
                    </div>
                    
                    {data.profile.realName && (
                      <p className="text-muted-foreground mb-3 text-lg">{data.profile.realName}</p>
                    )}
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm mb-4">
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-lg">
                        {getVisibilityIcon(data.profile.communityVisibilityState)}
                        <span className="font-medium">{getVisibilityText(data.profile.communityVisibilityState)}</span>
                      </div>
                      {data.profile.locCountryCode && (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-lg">
                          <MapPin className="h-4 w-4" />
                          <span className="font-medium">{data.profile.locCountryCode}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-lg">
                        <Calendar className="h-4 w-4" />
                        <span className="font-medium">
                          Member since {new Date(data.profile.timeCreated * 1000).getFullYear()}
                        </span>
                      </div>
                      {data.profile.lastLogoff > 0 && (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-lg">
                          <Clock className="h-4 w-4" />
                          <span className="font-medium">
                            Last seen {new Date(data.profile.lastLogoff * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                      )}
                    </div>

                    {data.profile.gameExtraInfo && (
                      <div className="mb-4 p-3 bg-primary/10 border border-primary/20 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Play className="h-4 w-4 text-primary" />
                          <span className="text-sm font-medium">Currently playing: {data.profile.gameExtraInfo}</span>
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button asChild size="default">
                        <a
                          href={data.profile.profileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          View on Steam
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>
              ) : gameAnalytics && (
                <div className="flex items-start gap-6">
                  {gameAnalytics.app?.header_image && (
                    <img
                      src={getProxiedImageUrl(gameAnalytics.app.header_image)}
                      alt={gameAnalytics.app.name}
                      className="w-24 h-24 rounded object-cover"
                      onError={handleImageError}
                    />
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-2xl font-bold">{gameAnalytics.app?.name}</h2>
                      <Badge variant="default">Game Analysis</Badge>
                    </div>
                    
                    {gameAnalytics.app?.short_description && (
                      <p className="text-muted-foreground mb-2">{gameAnalytics.app.short_description}</p>
                    )}
                    
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                      {gameAnalytics.currentPlayers && (
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          <span>{gameAnalytics.currentPlayers.toLocaleString()} current players</span>
                        </div>
                      )}
                      {gameAnalytics.app?.release_date && (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span>Released {gameAnalytics.app.release_date.date}</span>
                        </div>
                      )}
                      {gameAnalytics.app?.price_overview && gameAnalytics.app.price_overview.final && (
                        <div className="flex items-center gap-1">
                          <span>${(typeof gameAnalytics.app.price_overview.final === 'number' ? gameAnalytics.app.price_overview.final / 100 : 0).toFixed(2)}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button asChild size="sm">
                        <a
                          href={gameAnalytics.sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          View on Steam Store
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Statistics */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {data && data.games ? (
              <>
                <Card className="border-2 hover:border-blue-500 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                            <Gamepad2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Games</p>
                        </div>
                        <p className="text-3xl font-bold">{data.games.totalGames.toLocaleString()}</p>
                        {data.games.ownedGames.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {data.games.ownedGames.length} displayed
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 hover:border-green-500 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                            <Clock className="h-5 w-5 text-green-600 dark:text-green-400" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Playtime</p>
                        </div>
                        {(() => {
                          const playtime = formatPlaytimeDetailed(data.games.totalPlaytime);
                          return (
                            <>
                              <p className="text-3xl font-bold">{playtime.primary}</p>
                              <p className="text-sm text-muted-foreground capitalize">{playtime.unit}</p>
                              {playtime.secondary && (
                                <p className="text-xs text-muted-foreground mt-1">{playtime.secondary}</p>
                              )}
                            </>
                          );
                        })()}
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs text-muted-foreground">
                            Avg: {formatPlaytime(Math.floor(data.games.totalPlaytime / Math.max(data.games.totalGames, 1)))} per game
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 hover:border-purple-500 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                            <Users className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Friends</p>
                        </div>
                        <p className="text-3xl font-bold">{data.social.friendsCount.toLocaleString()}</p>
                        {data.social.friends.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {data.social.friends.length} displayed
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 hover:border-orange-500 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                            <TrendingUp className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Recent Activity</p>
                        </div>
                        <p className="text-3xl font-bold">{data.recentActivity.recentGames.length}</p>
                        <p className="text-sm text-muted-foreground capitalize">Last 2 weeks</p>
                        {data.recentActivity.recentGames.length > 0 && (() => {
                          const recentPlaytime = data.recentActivity.recentGames.reduce((sum, game) => sum + game.playtime2weeks, 0);
                          return (
                            <div className="mt-3 pt-3 border-t">
                              <p className="text-xs text-muted-foreground">
                                {formatPlaytime(recentPlaytime)} played recently
                              </p>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : gameAnalytics && (
              <>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-blue-600" />
                      <div>
                        <p className="text-2xl font-bold">{gameAnalytics.currentPlayers?.toLocaleString() || 'N/A'}</p>
                        <p className="text-sm text-muted-foreground">Current Players</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="text-2xl font-bold">{gameAnalytics.globalAchievements?.length || 0}</p>
                        <p className="text-sm text-muted-foreground">Achievements</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-purple-600" />
                      <div>
                        <p className="text-2xl font-bold">{gameAnalytics.news?.length || 0}</p>
                        <p className="text-sm text-muted-foreground">News Items</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2">
                      <Gamepad2 className="h-5 w-5 text-orange-600" />
                      <div>
                        <p className="text-2xl font-bold">{gameAnalytics.app?.steam_appid || 'N/A'}</p>
                        <p className="text-sm text-muted-foreground">App ID</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {/* Detailed Tabs - Only show for profile data */}
          {data && (
            <Tabs defaultValue="games" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="games">Games Library</TabsTrigger>
                <TabsTrigger value="recent">Recent Activity</TabsTrigger>
                <TabsTrigger value="social">Social</TabsTrigger>
              </TabsList>

            {/* Games Library */}
            <TabsContent value="games">
              <Card>
                <CardHeader>
                  <CardTitle>Games Library</CardTitle>
                  <CardDescription>
                    {data && data.games ? (
                      <>
                        {data.games.totalGames} games • {formatPlaytime(data.games.totalPlaytime)} total playtime
                      </>
                    ) : (
                      "Game analysis data"
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {data && data.games && data.games.ownedGames.length > 0 ? (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {data.games.ownedGames
                        .sort((a, b) => b.playtimeForever - a.playtimeForever)
                        .map((game, index) => (
                        <Card key={game.appId} className="hover:shadow-lg transition-all border-2 hover:border-primary/50">
                          <CardContent className="p-4">
                            <div className="space-y-3">
                              <div className="flex items-start gap-3">
                                {game.imgLogoUrl && (
                                  <div className="relative shrink-0">
                                    <img
                                      src={getProxiedImageUrl(`https://media.steampowered.com/steamcommunity/public/images/apps/${game.appId}/${game.imgLogoUrl}.jpg`)}
                                      alt={game.name}
                                      className="w-20 h-20 object-cover rounded-lg border-2"
                                      onError={handleImageError}
                                    />
                                    {index < 3 && (
                                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                                        {index + 1}
                                      </div>
                                    )}
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-semibold text-sm line-clamp-2 leading-tight">{game.name}</h3>
                                  <div className="mt-2 space-y-1">
                                    <div className="flex items-center gap-1">
                                      <Clock className="h-3 w-3 text-muted-foreground" />
                                      <p className="text-xs font-medium">
                                        {formatPlaytime(game.playtimeForever)} total
                                      </p>
                                    </div>
                                    {game.playtime2weeks > 0 && (
                                      <div className="flex items-center gap-1">
                                        <TrendingUp className="h-3 w-3 text-green-600" />
                                        <p className="text-xs text-green-600 font-medium">
                                          {formatPlaytime(game.playtime2weeks)} last 2 weeks
                                        </p>
                                      </div>
                                    )}
                                    {game.rtimeLastPlayed > 0 && (
                                      <p className="text-xs text-muted-foreground">
                                        Last played: {new Date(game.rtimeLastPlayed * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {game.playtimeForever > 0 && (
                                <div className="space-y-2 pt-2 border-t">
                                  <div className="flex justify-between items-center text-xs">
                                    <span className="text-muted-foreground">Playtime</span>
                                    <span className="font-semibold">{formatPlaytime(game.playtimeForever)}</span>
                                  </div>
                                  <Progress 
                                    value={Math.min((game.playtimeForever / Math.max(data.games.totalPlaytime / data.games.totalGames, 1)) * 10, 100)} 
                                    className="h-2"
                                  />
                                  <p className="text-xs text-muted-foreground">
                                    {((game.playtimeForever / data.games.totalPlaytime) * 100).toFixed(1)}% of total playtime
                                  </p>
                                </div>
                              )}

                              <div className="flex gap-2 pt-2">
                                <Button
                                  size="sm"
                                  variant="default"
                                  onClick={() => handleGetComprehensiveGameAnalytics(game.appId)}
                                  disabled={getGameAnalyticsMutation.isPending}
                                  className="flex-1"
                                >
                                  {getGameAnalyticsMutation.isPending ? (
                                    <>
                                      <Activity className="h-3 w-3 mr-1 animate-spin" />
                                      Loading...
                                    </>
                                  ) : (
                                    <>
                                      <TrendingUp className="h-3 w-3 mr-1" />
                                      Analytics
                                    </>
                                  )}
                                </Button>
                                <Button asChild size="sm" variant="outline">
                                  <a
                                    href={`https://store.steampowered.com/app/${game.appId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <ExternalLink className="h-4 w-4" />
                                  </a>
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      {data.games.totalGames > data.games.ownedGames.length && (
                        <div className="col-span-full text-center py-4 text-sm text-muted-foreground">
                          Showing top {data.games.ownedGames.length} games by playtime out of {data.games.totalGames} total games
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Gamepad2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No games found or profile is private</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Recent Activity */}
            <TabsContent value="recent">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Activity</CardTitle>
                  <CardDescription>
                    Games played in the last 2 weeks
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {data && data.recentActivity && data.recentActivity.recentGames.length > 0 ? (
                    <div className="space-y-3">
                      {data.recentActivity.recentGames
                        .sort((a, b) => b.playtime2weeks - a.playtime2weeks)
                        .map((game) => (
                        <Card key={game.appId} className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                              {game.imgLogoUrl && (
                                <img
                                  src={getProxiedImageUrl(`https://media.steampowered.com/steamcommunity/public/images/apps/${game.appId}/${game.imgLogoUrl}.jpg`)}
                                  alt={game.name}
                                  className="w-16 h-16 object-cover rounded-lg border-2 shrink-0"
                                  onError={handleImageError}
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold truncate">{game.name}</h3>
                                <div className="flex items-center gap-4 mt-2">
                                  <div className="flex items-center gap-1">
                                    <Play className="h-3 w-3 text-green-600" />
                                    <p className="text-sm font-medium text-green-600">
                                      {formatPlaytime(game.playtime2weeks)}
                                    </p>
                                    <span className="text-xs text-muted-foreground ml-1">last 2 weeks</span>
                                  </div>
                                </div>
                                <div className="mt-2">
                                  <div className="flex items-center justify-between text-xs">
                                    <span className="text-muted-foreground">Total playtime</span>
                                    <span className="font-semibold">{formatPlaytime(game.playtimeForever)}</span>
                                  </div>
                                  {game.playtimeForever > 0 && (
                                    <Progress 
                                      value={(game.playtime2weeks / game.playtimeForever) * 100} 
                                      className="h-1.5 mt-1"
                                    />
                                  )}
                                </div>
                              </div>
                              <Button asChild size="sm" variant="outline" className="shrink-0">
                                <a
                                  href={`https://store.steampowered.com/app/${game.appId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </a>
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No recent activity or profile is private</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Social */}
            <TabsContent value="social">
              <Card>
                <CardHeader>
                  <CardTitle>Social</CardTitle>
                  <CardDescription>
                    {data && data.social ? (
                      <>
                        {data.social.friendsCount} friends
                      </>
                    ) : (
                      "Social data not available for game analysis"
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {data && data.social && data.social.friends.length > 0 ? (
                    <div className="space-y-3">
                      {data.social.friends.map((friend) => (
                        <Card key={friend.steamId} className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                              <div className="relative">
                                <div className="w-12 h-12 bg-linear-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                                  <User className="h-6 w-6" />
                                </div>
                                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-background rounded-full"></div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold truncate">Steam ID: {friend.steamId}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Calendar className="h-3 w-3 text-muted-foreground" />
                                  <p className="text-sm text-muted-foreground">
                                    Friends since {new Date(friend.friendSince * 1000).toLocaleDateString('en-US', { 
                                      year: 'numeric', 
                                      month: 'short', 
                                      day: 'numeric' 
                                    })}
                                  </p>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {Math.floor((Date.now() / 1000 - friend.friendSince) / (365.25 * 24 * 60 * 60))} years ago
                                </p>
                              </div>
                              <Button asChild size="sm" variant="outline" className="shrink-0">
                                <a
                                  href={`https://steamcommunity.com/profiles/${friend.steamId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  View
                                </a>
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      {data.social.friendsCount > data.social.friends.length && (
                        <div className="text-center py-4 text-sm text-muted-foreground">
                          Showing {data.social.friends.length} of {data.social.friendsCount} friends
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No friends found or profile is private</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
          )}

          {/* Game Details Modal */}
          {selectedGame && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gamepad2 className="h-5 w-5" />
                  {selectedGame.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="overview" className="w-full">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="news">News</TabsTrigger>
                    <TabsTrigger value="achievements">Achievements</TabsTrigger>
                    <TabsTrigger value="stats">Stats</TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview">
                    <div className="space-y-6">
                      <div className="flex items-start gap-6">
                        {selectedGame.headerImage && (
                          <img
                            src={getProxiedImageUrl(selectedGame.headerImage)}
                            alt={selectedGame.name}
                            className="w-32 h-18 object-cover rounded"
                            onError={handleImageError}
                          />
                        )}
                        <div className="flex-1">
                          <p className="text-muted-foreground mb-4">{selectedGame.description}</p>
                          
                          <div className="grid gap-4 md:grid-cols-2">
                            {selectedGame.developers && selectedGame.developers.length > 0 && (
                              <div>
                                <h4 className="font-semibold mb-2">Developers</h4>
                                <div className="flex flex-wrap gap-2">
                                  {selectedGame.developers.map((dev: string, index: number) => (
                                    <Badge key={index} variant="secondary">{dev}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {selectedGame.publishers && selectedGame.publishers.length > 0 && (
                              <div>
                                <h4 className="font-semibold mb-2">Publishers</h4>
                                <div className="flex flex-wrap gap-2">
                                  {selectedGame.publishers.map((pub: string, index: number) => (
                                    <Badge key={index} variant="outline">{pub}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {selectedGame.genres && selectedGame.genres.length > 0 && (
                              <div>
                                <h4 className="font-semibold mb-2">Genres</h4>
                                <div className="flex flex-wrap gap-2">
                                  {selectedGame.genres.map((genre: any, index: number) => (
                                    <Badge key={index} variant="default">{genre.description}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {selectedGame.playerCount && (
                              <div>
                                <h4 className="font-semibold mb-2">Current Players</h4>
                                <p className="text-2xl font-bold text-green-600">
                                  {selectedGame.playerCount.result?.toLocaleString() || 'N/A'}
                                </p>
                              </div>
                            )}
                          </div>

                          <div className="flex gap-2 mt-6">
                            <Button asChild>
                              <a
                                href={`https://store.steampowered.com/app/${selectedGame.appId || selectedGame.steam_appid}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <ExternalLink className="h-4 w-4 mr-2" />
                                View on Steam Store
                              </a>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="news">
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold">Game News</h3>
                        <Button
                          onClick={() => {
                            const appId = selectedGame.appId || selectedGame.steam_appid;
                            if (appId && !isNaN(parseInt(appId.toString()))) {
                              handleGetGameNews(parseInt(appId.toString()));
                            } else {
                              setError("Invalid App ID for this game");
                            }
                          }}
                          disabled={getGameNewsMutation.isPending}
                          size="sm"
                        >
                          {getGameNewsMutation.isPending ? (
                            <>
                              <Activity className="h-4 w-4 mr-2 animate-spin" />
                              Loading...
                            </>
                          ) : (
                            <>
                              <Calendar className="h-4 w-4 mr-2" />
                              Load News
                            </>
                          )}
                        </Button>
                      </div>

                      {gameNews?.newsitems ? (
                        <div className="space-y-4">
                          {gameNews.newsitems.map((news: any, index: number) => (
                            <Card key={index}>
                              <CardContent className="p-4">
                                <div className="space-y-2">
                                  <h4 className="font-semibold">{news.title}</h4>
                                  <p className="text-sm text-muted-foreground line-clamp-3">
                                    {news.contents}
                                  </p>
                                  <div className="flex justify-between items-center text-xs text-muted-foreground">
                                    <span>{new Date(news.date * 1000).toLocaleDateString()}</span>
                                    <Button asChild size="sm" variant="outline">
                                      <a href={news.url} target="_blank" rel="noopener noreferrer">
                                        <ExternalLink className="h-3 w-3 mr-1" />
                                        Read More
                                      </a>
                                    </Button>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>Click "Load News" to fetch the latest game news</p>
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="achievements">
                    <div className="space-y-4">
                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            const appId = selectedGame.appId || selectedGame.steam_appid;
                            if (appId && !isNaN(parseInt(appId.toString()))) {
                              handleGetGlobalAchievements(parseInt(appId.toString()));
                            } else {
                              setError("Invalid App ID for this game");
                            }
                          }}
                          disabled={getGlobalAchievementsMutation.isPending}
                          size="sm"
                        >
                          {getGlobalAchievementsMutation.isPending ? (
                            <>
                              <Activity className="h-4 w-4 mr-2 animate-spin" />
                              Loading...
                            </>
                          ) : (
                            <>
                              <Trophy className="h-4 w-4 mr-2" />
                              Global Achievements
                            </>
                          )}
                        </Button>
                        {data && (
                          <Button
                            onClick={() => {
                              const appId = selectedGame.appId || selectedGame.steam_appid;
                              if (appId && !isNaN(parseInt(appId.toString()))) {
                                handleGetPlayerAchievements(parseInt(appId.toString()), data.profile.steamId);
                              } else {
                                setError("Invalid App ID for this game");
                              }
                            }}
                            disabled={getPlayerAchievementsMutation.isPending}
                            size="sm"
                            variant="outline"
                          >
                            {getPlayerAchievementsMutation.isPending ? (
                              <>
                                <Activity className="h-4 w-4 mr-2 animate-spin" />
                                Loading...
                              </>
                            ) : (
                              <>
                                <Award className="h-4 w-4 mr-2" />
                                My Achievements
                              </>
                            )}
                          </Button>
                        )}
                      </div>

                      {globalAchievements?.achievements && (
                        <div className="space-y-4">
                          <h4 className="font-semibold">Global Achievement Percentages</h4>
                          <div className="grid gap-2">
                            {globalAchievements.achievements.slice(0, 10).map((achievement: any, index: number) => {
                              // Debug log to see achievement data
                              console.log('Achievement data:', achievement);
                              return (
                              <div key={index} className="flex items-center justify-between p-3 border rounded">
                                <div className="flex-1">
                                  <p className="font-medium text-sm">{achievement.name}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-bold">{typeof achievement.percent === 'number' ? achievement.percent.toFixed(1) : '0.0'}%</p>
                                  <Progress 
                                    value={typeof achievement.percent === 'number' ? Math.max(achievement.percent, 0.1) : 0} 
                                    className="w-20 h-2" 
                                  />
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {playerAchievements?.achievements && (
                        <div className="space-y-4">
                          <h4 className="font-semibold">Your Achievements</h4>
                          <div className="grid gap-2">
                            {playerAchievements.achievements.slice(0, 10).map((achievement: any, index: number) => (
                              <div key={index} className={`flex items-center justify-between p-3 border rounded ${achievement.achieved ? 'bg-green-50 border-green-200' : 'bg-gray-50'}`}>
                                <div className="flex-1">
                                  <p className="font-medium text-sm">{achievement.name}</p>
                                  <p className="text-xs text-muted-foreground">{achievement.description}</p>
                                </div>
                                <div className="text-right">
                                  {achievement.achieved ? (
                                    <div className="flex items-center gap-1 text-green-600">
                                      <CheckCircle className="h-4 w-4" />
                                      <span className="text-xs">Unlocked</span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1 text-gray-400">
                                      <Clock className="h-4 w-4" />
                                      <span className="text-xs">Locked</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {!globalAchievements && !playerAchievements && (
                        <div className="text-center py-8 text-muted-foreground">
                          <Trophy className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>Click the buttons above to load achievement data</p>
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="stats">
                    <div className="space-y-4">
                      {data && (
                        <Button
                          onClick={() => {
                            const appId = selectedGame.appId || selectedGame.steam_appid;
                            if (appId && !isNaN(parseInt(appId.toString()))) {
                              handleGetUserStats(parseInt(appId.toString()), data.profile.steamId);
                            } else {
                              setError("Invalid App ID for this game");
                            }
                          }}
                          disabled={getUserStatsMutation.isPending}
                          size="sm"
                        >
                          {getUserStatsMutation.isPending ? (
                            <>
                              <Activity className="h-4 w-4 mr-2 animate-spin" />
                              Loading...
                            </>
                          ) : (
                            <>
                              <TrendingUp className="h-4 w-4 mr-2" />
                              Load My Stats
                            </>
                          )}
                        </Button>
                      )}

                      {userStats?.stats && (
                        <div className="space-y-4">
                          <h4 className="font-semibold">Your Game Statistics</h4>
                          <div className="grid gap-4 md:grid-cols-2">
                            {userStats.stats.map((stat: any, index: number) => (
                              <Card key={index}>
                                <CardContent className="p-4">
                                  <div className="space-y-2">
                                    <h5 className="font-medium text-sm">{stat.name}</h5>
                                    <p className="text-2xl font-bold">{stat.value}</p>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        </div>
                      )}

                      {userStats?.achievements && (
                        <div className="space-y-4">
                          <h4 className="font-semibold">Achievement Progress</h4>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span>Total Achievements</span>
                              <span className="font-bold">{userStats.achievements.length}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span>Completed</span>
                              <span className="font-bold text-green-600">
                                {userStats.achievements.filter((a: any) => a.achieved).length}
                              </span>
                            </div>
                            <Progress 
                              value={userStats.achievements.length > 0 ? (userStats.achievements.filter((a: any) => a.achieved).length / userStats.achievements.length) * 100 : 0} 
                              className="h-2"
                            />
                          </div>
                        </div>
                      )}

                      {!userStats && (
                        <div className="text-center py-8 text-muted-foreground">
                          <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>Click "Load My Stats" to fetch your game statistics</p>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
